import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, FormGroup } from '@angular/forms';
import * as _ from 'lodash-es';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { Subscription } from 'rxjs';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { F1120SchBService } from './f1120-sch-b.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-f1120-sch-b',
    templateUrl: './f1120-sch-b.component.html',
    styleUrls: ['./f1120-sch-b.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F1120SchBComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  filterParams: any = {};

  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};

  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  rowData: any = {};

  private isInitialized = false;

  // Component properties migrated from F1120ScheduleBCtrl.js
  modalTitle = '';
  originalData: any[] = [];
  userSettings: any;
  logged_in_user: string = '';
  leId: string = '';
  formName = '1120';
  hgt = '96%';
  cdr_no: string = '';
  leType: string = '';
  systemFlag = 'D';
  showFormLoader = true;
  crudLoading = false;
  isSaveClicked = false;
  
  // Entity information
  entityList: any[] = [];

  // Form data structures (maintaining AngularJS structure)
  formData: any = {
    "addrows": [],
    "editrows": [],
    "removerows": []
  };
  
  scheduleBForm: { rows: any[] } = { rows: [] };
  scheduleBFormView: any[] = [];
  mainData: any[] = [];
  tableHeader: any[] = [];

  // Tab configuration (maintaining AngularJS structure)
  tabs = [
    { id: 1, name: "Form Entry", active: true }
  ];
  activeTab = 1;
  currentTabName = "Form Entry";
  
  // Modal minimization state
  minimise = false;

  constructor(
    private jsonObjectFactory: JsonObjectFactoryService,
    private f1120SchBService: F1120SchBService,
    private _snackBar: MatSnackBar, private gtwSnackbar: GtwSnackbarService,
    private formBuilder: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal) as GtwElementsBaseUrlsModel;
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.appService.USER_SETTINGS = header.userSettingsData;
    this.appService.SERVER_CONFIG = header.serverConfigData;
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.globalService.filterParams = this.filterParams;
    this.isInitialized = false;
    this.cdr.markForCheck();
  }

  init() {
    try {
      if (!this.globalService || !this.GENERAL_CONFIG) {
        return;
      }

      this.userSettings = this.appService.USER_SETTINGS;
      this.logged_in_user = this.userSettings?.user?.sso_id || '';
      
      if (this.rowData && Object.keys(this.rowData).length > 0 && !this.isInitialized) {
        this.isInitialized = true;
        this.setupEntityData();
        this.initSchB();
      }
    } catch (e) {
      console.error('Error in F1120SchB init:', e);
    }
  }

  private setupEntityData() {
    this.leId = this.rowData.LEID;
    this.cdr_no = this.rowData.MARS;
    this.leType = this.rowData.LE_TYPE;
    
    this.entityList = [
      {key: "Tax Year", value: this.rowData.TAX_YEAR},
      {key: "LEID", value: this.rowData.LEID},
      {key: "LE Name", value: this.rowData.LE_NAME}
    ];
  }

  initSchB() {
    this.fetchSchBrecords();
  }

  fetchSchBrecords() {
    const params = { 
      "action_id": 31333, 
      "tax_year": this.rowData.TAX_YEAR,
      "scenario": this.rowData.SCENARIO,
      "jcd_key": this.rowData.JCD_KEY,
      "combination_key": this.rowData.COMBINATION_KEY,
      "form_name": "F1120", 
      "schedule_name": "B", 
      "part_no": "NA"
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadPivotFormJson',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.processFormData(data);
      this.showFormLoader = false;
      this.cdr.markForCheck();
    });
  }

  private processFormData(data: any) {
    const scheduleBForm: { rows: any[] } = { rows: [] };
    let saveParamDtls: any[] = [];
    let tempDataObj: any = {};

    for (let i = 1; i < data.irsForm.formItemList.length; i++) {
      saveParamDtls = [];
      tempDataObj = {};
      saveParamDtls = data.irsForm.formItemList[i].dataRowList[0].rowDimensions.object_id.split('~');
      const rows = data.irsForm.formItemList[i].dataRowList[0].dataCellList.length;
      
      tempDataObj.num = data.irsForm.formItemList[i].dataRowList[0].dataCellList[0].name;
      tempDataObj.description = data.irsForm.formItemList[i].dataRowList[0].dataCellList[1].name;
      
      tempDataObj.line_order = saveParamDtls[8];
      tempDataObj.saveParams = saveParamDtls;
      
      if (rows > 2) {
        this.iterateArray(rows, data.irsForm.formItemList[i].dataRowList[0].dataCellList, tempDataObj);
      }
      
      scheduleBForm.rows.push(tempDataObj);
      
      if (i === 1) {
        this.modalTitle = data.irsForm.formItemList[0].dataRowList[0].dataCellList[1].name;
      }
    }

    this.tableHeader = scheduleBForm.rows.filter(row => row.num === "NA");
    this.scheduleBFormView = scheduleBForm.rows.filter(row => row.num !== 'NA');
    
    if (this.tableHeader.length > 0) {
      this.tableHeader[0].value = this.rowData.LE_NAME;
    }
    if (this.tableHeader.length > 1) {
      this.tableHeader[1].value = this.rowData.EIN;
    }
    
    this.originalData = _.cloneDeep(this.scheduleBFormView);
    this.initFormView(this.scheduleBFormView);
  }

  private iterateArray(count: number, array: any[], tempDataObj: any) {
    if (count === 3) {
      tempDataObj.lineAttrKey_a = array[2].key.split('~')[1];
      tempDataObj.attrib_name_a = array[2].key.split('~')[0];
      tempDataObj.attrib_order_a = array[2].key.split('~')[2];
      tempDataObj.is_form_display = tempDataObj.saveParams && tempDataObj.saveParams.length > 9 ? tempDataObj.saveParams[9] : '';
      
      tempDataObj.edit_type = array[2].property.edit_type;
      tempDataObj.data_type = array[2].property.data_type;
      
      if (array[2].value == null) {
        tempDataObj.value = '';
      } else {
        tempDataObj.value = array[2].value;
      }
      
    } else if (count === 4) {
      tempDataObj.lineAttrKey_a = array[2].key.split('~')[1];
      tempDataObj.attrib_name_a = array[2].key.split('~')[0];
      tempDataObj.attrib_order_a = array[2].key.split('~')[2];
      tempDataObj.edit_type = array[2].property.edit_type;
      tempDataObj.data_type = array[2].property.data_type;

      tempDataObj.lineAttrKey_b = array[3].key.split('~')[1];
      tempDataObj.attrib_name_b = array[3].key.split('~')[0];
      tempDataObj.attrib_order_b = array[3].key.split('~')[2];

      if (array[2].value === "true") {
        tempDataObj.value = true;
      } else if (array[3].value === "true") {
        tempDataObj.value = false;
      } else {
        tempDataObj.value = 'NA';
      }
    }
  }

  fieldVisibility(index: number, state: boolean, name: string, rowNum: string) {
    this.scheduleBFormView[index].conditionalField = !this.scheduleBFormView[index].conditionalField;
    
    if ((name === 'yes' && !state) || (name === 'no' && state)) {
      this.scheduleBFormView[index].value = 'NA';
    }
  }

  limitDecimal(val: number, line: string, i: number, attr: string) {
    const index = this.scheduleBFormView.findIndex(row => row.num === line);
    if (Number(val) > 100 && index !== -1) {
      this.scheduleBFormView[index].JSON_DATA[i][attr] = 100;
    }
  }

  save() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    const tempArray: any[] = [];
    
    for (let i = 0; i < this.scheduleBFormView.length; i++) {
      const row = this.scheduleBFormView[i];
      const trans_type_key = row.saveParams?.[1];

      const formRowDataA: any = {
        tax_year: this.rowData.TAX_YEAR,
        trans_type_key: trans_type_key,
        line_order: row.line_order,
        attrib_order: row.attrib_order_a,
        line_description: row.description,
        combination_key: this.rowData.COMBINATION_KEY,
        group_obj_key: this.rowData.GROUP_OBJ_KEY,
        form_key: row.saveParams[2],
        line_no: row.num,
        occurence: row.saveParams[7],
        line_attrib_key: row.lineAttrKey_a,
        Attribute_Name: row.attrib_name_a,
        Attribute_Value: row.value === true ? 'true' : 
                        row.value === 'NA' ? 'false' : 
                        row.value === false ? 'false' : 
                        row.value ? row.value.toString() : '',
        trans_dtls_key: row.saveParams[3],
        trans_status: row.saveParams[6],
        is_dirty: ''
      };
      formRowDataA.JSON_DATA = [];
      tempArray.push(formRowDataA);

      if (typeof row.attrib_name_b !== "undefined") {
        const formRowDataB: any = {
          tax_year: this.rowData.TAX_YEAR,
          trans_type_key: row.saveParams[1],
          line_order: row.line_order,
          attrib_order: row.attrib_order_b,
          line_description: row.description,
          combination_key: this.rowData.COMBINATION_KEY,
          group_obj_key: this.rowData.GROUP_OBJ_KEY,
          form_key: row.saveParams[2],
          line_no: row.num,
          occurence: row.saveParams[7],
          line_attrib_key: row.lineAttrKey_b,
          Attribute_Name: row.attrib_name_b,
          Attribute_Value: row.value === true ? 'false' : 
                          row.value === 'NA' ? 'false' : 
                          row.value === false ? 'true' : 
                          row.value ? row.value.toString() : '',
          trans_dtls_key: row.saveParams[3],
          trans_status: row.saveParams[6],
          is_dirty: ''
        };
        tempArray.push(formRowDataB);
      }
    }

    this.formData.editrows = [tempArray];
    this.mainData = [this.formData];

    this.f1120SchBService.saveF1120SchBForm(
      this.GENERAL_CONFIG.base_url,
      this.mainData,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        
        if (response.callSuccess === "1" && response.errorMessage === "null") {
          this.gtwSnackbar.openSnackBar('Form has been successfully saved/updated', 'gtw-snackbar--green', true, 4);
          
          this.requestGridUpdate.emit('refresh');
        } else {
          this.isSaveClicked = false;
          if (response.errorMessage && response.errorMessage !== 'null') {
            this.gtwSnackbar.openSnackBar(response.errorMessage, 'gtw-snackbar--red', true, 4);
          } else {
            this.gtwSnackbar.openSnackBar('Error occurred in saving the Transaction. If this continues please contact support', 'gtw-snackbar--red', true, 4);
          }
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        this.gtwSnackbar.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red', true, 4);
        this.cdr.markForCheck();
      }
    );
  }

  reset() {
    this.scheduleBFormView = _.cloneDeep(this.originalData);
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  onRadioChange(index: number, value: boolean, row: any) {
    row.value = value;
    this.fieldVisibility(index, value, row.description, row.num);
  }

  private initFormView(filteredFormData: any[]) {
    this.scheduleBFormView = filteredFormData;
  }

  itemTracker(index: number, item: any): any {
    return item ? item.lineAttrKey_a || item.LEID || index : index;
  }
}
